// @ts-nocheck
/* ============================================================
   The words an automation sends, per service (plan §5.6, roadmap §13 item 7).

   Every default message used to be written for a fence job ("your fence
   install is tomorrow"), and a concrete customer got it word for word. This is
   the one table of defaults, keyed by the automation's definition id, with a
   fence column and a concrete column. The fence column is byte-identical to
   the strings the AI screen and the server shipped with — nothing a fence
   workspace sends changes.

   How a template resolves for a job of service `v` (resolveTemplate):
     1. the contractor's own words for that service — config.byVertical[v].template
     2. a fence job: config.template (whatever the contractor saved), else the default
     3. another service: config.template only when the contractor wrote it
        themselves AND it doesn't say "fence" — the materialized fence default
        (lib/supabase.ts persistAI writes it on first save) is not their words
     4. the service's default, falling back to the fence default

   Import-free and mirrored by server/src/servicewords.js; unit-servicewords.mjs
   pins the two tables equal and the fence column equal to the legacy map.
   ============================================================ */

export const DEFAULT_TEMPLATES: Record<string, { fence: string; concrete: string }> = {
  missed_call: {
    fence: "Hi {customer}, sorry we missed your call! Get an instant fence estimate here: {link}",
    concrete: "Hi {customer}, sorry we missed your call! Tell us about your concrete and get a price by text: {link}",
  },
  auto_review: {
    fence: "Thanks for choosing {company}, {customer}! If you have 30 seconds, we'd love a quick review: {link}",
    concrete: "Thanks for choosing {company}, {customer}! If you have 30 seconds, we'd love a quick review: {link}",
  },
  neg_review: {
    fence: "Hi {customer}, how did we do on your fence? Tap here to let us know: {link}",
    concrete: "Hi {customer}, how did we do on your concrete? Tap here to let us know: {link}",
  },
  // A phone-captured estimate is a REQUEST until someone confirms it: nothing
  // checks availability or holds a slot (see server/src/db.js bookEstimate).
  estimate_confirm: {
    fence: "Hi {customer}, thanks for calling {company} — we have your estimate request{forWhen} and we'll text you to lock in the time. Reply R if a different day suits you better.",
    concrete: "Hi {customer}, thanks for calling {company} — we have your estimate request{forWhen} and we'll text you to lock in the time. Reply R if a different day suits you better.",
  },
  appt_reminder: {
    fence: "Hi {customer}, reminder: your {company} fence install is tomorrow, {date} at {time}. Please unlock any gates and keep pets inside. Reply R if you need to move it.",
    concrete: "Hi {customer}, reminder: {company} is coming tomorrow, {date} at {time}. Please have the concrete clear of cars and anything sitting on it, and keep pets inside. Reply R if you need to move it.",
  },
  appt_confirm: {
    fence: "Hi {customer}, your {company} install is set for {date} at {time}. Reply C to confirm, or R if you need a different day.",
    concrete: "Hi {customer}, your {company} appointment is set for {date} at {time}. Reply C to confirm, or R if you need a different day.",
  },
  on_my_way: {
    fence: "Hi {customer}, {company} is on the way — see you soon!",
    concrete: "Hi {customer}, {company} is on the way — see you soon!",
  },
  diggers: {
    fence: "811 locate reminder: file for {customer}'s job before the crew digs on {date}.",
    concrete: "811 locate reminder: file for {customer}'s job before the crew digs on {date}.",
  },
  farming: {
    fence: "Hi neighbor — {company} just built a fence nearby. Want a free estimate for yours? {link}",
    concrete: "Hi neighbor — {company} just leveled a driveway nearby. Sunken or cracked concrete at your place? Get a price from a few photos: {link}",
  },
  invoice: {
    fence: "Hi {customer}, a friendly reminder your balance with {company} is due. Pay securely here: {link}",
    concrete: "Hi {customer}, a friendly reminder your balance with {company} is due. Pay securely here: {link}",
  },
  speed_lead: {
    fence: "Hi {customer}, thanks for reaching out to {company}! Here's your instant fence estimate: {link}",
    concrete: "Hi {customer}, thanks for reaching out to {company}! We have your request and we'll text you a firm price within 24 hours. Add photos or details here: {link}",
  },
  job_status: {
    fence: "Hi {customer}, your {company} crew is on the way! Track updates here: {link}",
    concrete: "Hi {customer}, your {company} crew is on the way! Track updates here: {link}",
  },
  photo_capture: {
    fence: "Hi {customer}, your fence looks great! Mind if we grab a quick photo for our portfolio?",
    concrete: "Hi {customer}, your concrete looks great! Mind if we grab a quick photo for our portfolio?",
  },
  financing: {
    fence: "Hi {customer}, you can split your {company} fence into easy monthly payments — pre-qualify (no credit hit): {link}",
    concrete: "Hi {customer}, you can split your {company} project into easy monthly payments — pre-qualify (no credit hit): {link}",
  },
  abandoned_quote: {
    fence: "Hi {customer}, your fence estimate from {company} is saved and ready — finish up here: {link}",
    concrete: "Hi {customer}, your concrete estimate from {company} is saved and ready — finish up here: {link}",
  },
  pipeline: {
    fence: "{company} today: leads in, quotes out, jobs booked, and cash collected.",
    concrete: "{company} today: leads in, quotes out, jobs booked, and cash collected.",
  },
  warranty: {
    fence: "Hi {customer}, it's been a while since {company} built your fence — want a free check-up or re-seal? {link}",
    concrete: "Hi {customer}, it's been about a year since {company} worked on your concrete. Sealer and caulk wear with every winter — want a free check-up or a fresh coat? {link}",
  },
  // the weather reschedule note (server WEATHER_TEMPLATE; approval.test pins the fence wording)
  weather: {
    fence: "Hi {customer}, {company} here — the forecast for {date} is looking wet, so we may need to move your install. We'll confirm a new day as soon as we can. Sorry for the shuffle!",
    concrete: "Hi {customer}, {company} here — the forecast for {date} isn't right for {work} (it needs a dry slab and temps above 40°F to bond), so we may need to move your appointment. We'll confirm a new day as soon as we can. Sorry for the shuffle!",
  },
  // the quote follow-up's "before it expires" reminder (quotedetails.test pins the fence wording)
  quote_expiry: {
    fence: "Hi {customer}, a heads-up from {company}: your fence quote is good through {expires}. If you'd like to go ahead, you can review and sign it here: {link} — or reply with any questions.",
    concrete: "Hi {customer}, a heads-up from {company}: your concrete quote is good through {expires}. If you'd like to go ahead, you can review and sign it here: {link} — or reply with any questions.",
  },
  // the cross-sell offer (upsell): {noticed} is what the crew ticked on site,
  // {company} and {link} are the OTHER service's — the brand that does that work
  upsell: {
    fence: "Hi {customer}, thanks again for having us out! One thing we noticed on site: {noticed}. {company} takes care of that too — want a free quote? {link}",
    concrete: "Hi {customer}, thanks again for having us out! One thing we noticed on site: {noticed}. {company} takes care of that too — want a free quote? {link}",
  },
};

/* the customer job updates, one per moment (automations.js job_status) */
export const JOB_STATUS_TEMPLATES: Record<string, { fence: string; concrete: string }> = {
  enroute: {
    fence: "Hi {customer}, your {company} crew is on the way — see you soon!",
    concrete: "Hi {customer}, your {company} crew is on the way — see you soon!",
  },
  started: {
    fence: "Hi {customer}, we’ve started on your fence today. We’ll let you know when it’s done.",
    concrete: "Hi {customer}, we’ve started on your concrete today. We’ll let you know when it’s done.",
  },
  completed: {
    fence: "Hi {customer}, your fence is finished! Take a look and tell us what you think.",
    concrete: "Hi {customer}, your concrete is done! Mind the cure times we left you, and tell us what you think.",
  },
  delayed: {
    fence: "Hi {customer}, we’ve had to push your {company} install. We’ll call shortly with a new date — sorry for the shuffle.",
    concrete: "Hi {customer}, we’ve had to push your {company} appointment. We’ll call shortly with a new date — sorry for the shuffle.",
  },
};

export const GENERIC_TEMPLATE = "Hi {customer}, this is {company} — {link}";

/* The cross-sell rules a workspace starts with. The fence-only trio is what the
   card showed before; a workspace that runs concrete gets the pair that the crew
   checklist can actually tick (CROSS_SELL_CHECKS below). `then` names the work;
   targetVerticalOf reads which service does it. */
export const LEGACY_UPSELL_RULES = [
  { when: "2-car garage", then: "Epoxy garage floor" },
  { when: "Large backyard", then: "Deck / patio" },
  { when: "Old fence on site", then: "Tear-out & haul" },
];
export const CROSS_SELL_RULES = [
  { when: "Sunken or cracked concrete noted", then: "Concrete lifting quote" },
  { when: "Old or leaning fence noted", then: "New fence quote" },
  { when: "2-car garage", then: "Garage floor coating" },
];
/* the two observation ticks on the crew's on-site checklist that feed the
   rules (outbox.ts CROSS_SELL_CHECKS; server/src/fieldops.js mirrors the ids) */
export const CROSS_SELL_CHECK_IDS = ["xs_concrete", "xs_fence"];

const V = (v: any) => (v === "concrete" ? "concrete" : "fence");
const str = (v: any) => (typeof v === "string" ? v.trim() : "");

/* the default words for an automation (by definition id) and a service */
export function templateFor(def: any, vertical?: any): string {
  const row = DEFAULT_TEMPLATES[String(def || "")];
  if (!row) return GENERIC_TEMPLATE;
  return row[V(vertical)] || row.fence;
}

/* the words that go out for THIS job — see the rules at the top */
export function resolveTemplate(config: any, def: any, vertical?: any): string {
  const v = V(vertical);
  const c = config && typeof config === "object" ? config : {};
  const by = c.byVertical && typeof c.byVertical === "object" ? c.byVertical[v] : null;
  const own = by && typeof by === "object" ? str(by.template) : "";
  if (own) return own;
  const t = str(c.template);
  const fenceDefault = templateFor(def, "fence");
  if (v === "fence") return t || fenceDefault;
  if (t && t !== fenceDefault && !/\bfenc/i.test(t)) return t;
  return templateFor(def, v);
}

/* the per-service settings of an automation's config, merged over the shared
   ones: byVertical[v] wins for the keys it sets (threshold, checkInAfter, …) */
export function configFor(config: any, vertical?: any): any {
  const c = config && typeof config === "object" ? config : {};
  const v = V(vertical);
  if (v === "fence") return c;
  const by = c.byVertical && typeof c.byVertical === "object" && c.byVertical[v] && typeof c.byVertical[v] === "object" ? c.byVertical[v] : null;
  return by ? { ...c, ...by } : c;
}

/* which service does the work a cross-sell rule names — null when the words
   don't say (the offer then goes out under the job's own brand) */
export function targetVerticalOf(then: any): "fence" | "concrete" | null {
  const t = String(then || "").toLowerCase();
  if (/concrete|lift|level|coat|epoxy|seal|caulk|slab|driveway|garage floor|apron|patio/.test(t)) return "concrete";
  if (/fenc|gate|tear-out|tear out|picket|privacy/.test(t)) return "fence";
  return null;
}

/* "Sunken or cracked concrete noted" → "sunken or cracked concrete" (the {noticed} var) */
export function noticedOf(when: any): string {
  const w = String(when || "").trim().replace(/\s+(noted|on site|on-site)\s*$/i, "").trim();
  return w ? w.charAt(0).toLowerCase() + w.slice(1) : "something worth a look";
}

/* does a cross-sell rule match what the job carries? `job.noted` is what the
   crew ticked on site (job.completed); the older attribute names still count */
export function matchRule(when: any, job: any): boolean {
  const w = String(when || "").toLowerCase();
  const j = job && typeof job === "object" ? job : {};
  const noted = j.noted && typeof j.noted === "object" ? j.noted : {};
  if (w.includes("garage")) return !!(j.has_garage || noted.garage);
  if (w.includes("backyard") || w.includes("yard")) return (Number(j.yard_sqft) || 0) > 4000;
  if (w.includes("concrete") || w.includes("sunken") || w.includes("cracked") || w.includes("driveway") || w.includes("sidewalk")) return !!(j.sunken_concrete_noted || noted.concrete);
  if (w.includes("border") || w.includes("beds")) return !!j.beds_no_border;
  if (w.includes("fence")) return !!(j.existing_fence || noted.fence);
  return false;
}
